type FunctionReturnType<Return, Params extends unknown[]> = (...args: [...Params]) => Promise<Return>;

/**
 * Asynchronously retries the execution of a given function with specified arguments in case of failure, up to a maximum number of retries.
 *
 * @template Return - The expected return type of the function.
 * @template Params - An array of parameter types for the function.
 *
 * @param {FunctionReturnType<Return, Params>} fn - The function to execute asynchronously.
 * @param {...Params} args - The arguments to pass to the function when calling it.
 * @param {number} maxRetries - The maximum number of times to retry executing the function.
 * @param {(error: unknown) => boolean} [retryWhen] - An optional callback function that determines whether a retry should be attempted based on the error thrown.
 * @param {number} [retryCount=0] - The current retry attempt count (used internally for recursion).
 *
 * @returns {Promise<Awaited<ReturnType<FunctionReturnType<Return, Params>>>} - A promise that resolves to the return value of the successfully executed function.
 *
 * @throws {unknown} - If the maximum number of retries is reached or if the optional retryWhen callback rejects the retry attempt.
 */
export async function retryAsync<Return, Params extends unknown[]>(
  fn: FunctionReturnType<Return, Params>,
  args: [...Params],
  maxRetries: number,
  retryWhen?: (error: unknown) => boolean,
  retryCount = 0,
): Promise<Awaited<ReturnType<FunctionReturnType<Return, Params>>>> {
  const currRetry = retryCount;

  try {
    return await fn(...args);
  } catch (e) {
    if (currRetry < maxRetries && (!retryWhen || retryWhen?.(e))) {
      return retryAsync(fn, args, maxRetries, retryWhen, currRetry + 1);
    }
    throw e;
  }
}
